import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../shared/instrumentation/instrumented-http.service';
import { Cacheable } from '../shared/models/cacheable.model';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { OperationStream } from '../shared/models/operation-stream.model';
import { OfficeDotComOrigin } from '../shared/models/telemetry-constants.model';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { BrowserType, DeviceInfo, PlatformType } from '../shared/services/device-detector.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { SchedulerService } from '../shared/services/scheduler.service';
import { WindowService } from '../shared/services/window.service';
import { XhrService, XhrServiceError } from '../shared/services/xhr.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfig, ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { WorkloadPublicDataConfigService } from '../shared/shared-context/workload-publicdata-config.service';
import { ObservableHelper } from '../shared/utilities/observable-helper.utility';
import { InstallConfig, InstallConfigService, InstallResources } from './install-config.service';

export const enum ButtonScenario {
    DirectInstall = 'DirectInstall',
    GetMobileApps = 'GetMobileApps',
    RedirectToMyAccountPage = 'RedirectToMyAccountPage',
    Hide = 'Hide',
    Other = 'Other',
    SetupOffice = 'SetupOffice'
}

export interface InstallInfo {
    text: string;
    ariaLabel: string;
    url: string;
    scenario: ButtonScenario;
    appId?: string;
    cached?: boolean;
}

const enum CustomInstallerResultCode {
    Success = 0,
    Error,
    InvalidParam,
    InternalServerError
}

const enum InstallableSoftwareResultCode {
    Success = 0,
    NoLicense,
    InstallDisabled,
    Error,
    InvalidParam,
    InternalServerError
}

interface OfficeInstaller {
    Status: number;
    Url: string;
    Error?: string;
}

interface InstallableApp {
    Type: string;
    Id: string;
    Platform: string;
    PlatformVersion?: string;
    StaticDownloadUrl: string;
}

interface InstallableSoftware {
    Status: number;
    Apps: Array<InstallableApp>;
    Error?: string;
}

interface InstallableSoftwareStream extends Cacheable {
    readonly software: InstallableSoftware;
}

export interface CustomInstaller extends Cacheable {
    readonly installerUrl: string;
}

@Injectable({
    providedIn: 'root'
})
export class InstallDataService extends XhrService {

    private readonly installApiRoot = this.installConfigService.config.installApiRoot;
    private readonly productsSite = 'https://products.office.com';
    private readonly bitness = 'x86';
    private readonly bitnessDef = 'Def';
    private readonly config: HttpRequestOptions;
    private readonly installConfig: InstallConfig;
    private readonly resources: InstallResources;
    private readonly deferredResponses: Map<string, Promise<InstallInfo>>;
    private installableSoftwareStreamMap: Map<string, OperationStream<InstallableSoftwareStream>>;
    private customInstallerStreamMap: Map<string, OperationStream<CustomInstaller>>;
    private customInstallerPromise: Map<string, Promise<string>>;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly locService: LocService,
        private readonly installConfigService: InstallConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly windowService: WindowService,
        startupConfigService: ClientStartupConfigService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerPublicDataService: WorkloadPublicDataConfigService,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.Portal, contextService, officeHomeApiConfigService, platformLocation, scheduler);

        this.installConfig = this.installConfigService.config;
        this.resources = this.installConfig.resources;
        const startupConfig = startupConfigService.config;
        this.config = {
            headers: new Map([
                ['x-assistant-client-type', OfficeDotComOrigin],
                ['x-assistant-client-version', 'v1'],
                ['x-assistant-flights', startupConfig.sharedClientStartupContext.flights]
            ])
        };

        if (this.installConfig.sendRequestId) {
            this.config.headers.set('client-request-id', startupConfig.sharedClientStartupContext.correlationId);
        }

        if (this.inMockMode) {
            this.installApiRoot = contextService.sharedContext.officeHomeApiRoot;
        }

        this.deferredResponses = new Map<string, Promise<InstallInfo>>();
    }

    /**
     * Calls portal install API to fetch the list of installable apps. If an installable app is available, then we return InstallInfo corresponding
     * to the generic installer for that app the controller or view model to bind to view
     * @returns A promise with install info for the generic installer
     */
    public getGenericInstaller(officeMarket: string, deviceInfo: DeviceInfo, authType: AuthType, appFilter: string): Promise<InstallInfo> {
        if (this.deferredResponses.has(appFilter)) {
            return this.deferredResponses.get(appFilter)
                .then(info => {
                    info.cached = true;
                    return info;
                });
        }

        const deferredResponse = new Promise<InstallInfo>((resolve, reject) => {
            if (!this.resources) {
                resolve(this.getUnsupportedInstallInfo('installConfigService.config.resources is null'));
                return;
            }

            const locale = officeMarket.toLowerCase();
            const platform = deviceInfo.platform.platformType;

            if (platform === PlatformType.Linux) {
                resolve(this.getUnsupportedInstallInfo('Install is not supported on Linux'));
                return;
            }

            if (platform === PlatformType.Chromebook) {
                resolve(this.getUnsupportedInstallInfo('Install is not supported on Chromebooks'));
                return;
            }

            if (platform === PlatformType.Other) {
                resolve(this.getUnsupportedInstallInfo('Install is not supported on Unknown platform'));
                return;
            }

            if (platform !== PlatformType.PC && platform !== PlatformType.Mac) {
                // Prepare mobile experience
                // If PC installs are disabled for the user, hide Install Office button on mobile
                if (authType === AuthType.OrgId) {
                    if (!this.installApiRoot) {
                        reject('Failed to get installable software. Install API root is not defined');
                        return;
                    }
                    const pcInstallableSoftwareStream = this.getInstallableSoftwareStream(PlatformType[PlatformType.PC].toLowerCase(), locale, appFilter, this.bitness, null);
                    pcInstallableSoftwareStream.results.pipe(take(1)).toPromise().then(data => {
                        if (!data || !data.software) {
                            reject('No data returned due to failure in both cache and network requests.');
                            return;
                        }

                        const pcInstallableSoftware = data.software;
                        if (pcInstallableSoftware.Status === InstallableSoftwareResultCode.InstallDisabled
                            || pcInstallableSoftware.Status === InstallableSoftwareResultCode.NoLicense) {
                            resolve({
                                text: '',
                                ariaLabel: '',
                                url: '',
                                scenario: ButtonScenario.Hide
                            });
                        } else {
                            resolve(this.getMobileInstallerInfo(platform, `${this.productsSite}/${locale}/mobile/office-${deviceInfo.platform.pageName.toLowerCase()}`));
                        }
                    });

                    pcInstallableSoftwareStream.initialCompletion.then(error => {
                        if (!error) {
                            return;
                        }

                        if (!(error.cacheError && error.networkError)) {
                            this.globalErrorHandler.handleCacheObservableError(error, ErrorId.FMgqz, ErrorId.ZXObm);
                            return;
                        }

                        //Both network and cache request failed. Error details in Unhandled_Exception.
                        reject(error);
                    });
                } else {
                    resolve(this.getMobileInstallerInfo(platform, `${this.productsSite}/${locale}/mobile/office-${deviceInfo.platform.pageName.toLowerCase()}`));
                }
            } else {
                // Prepare desktop experience
                // Disable Install buttons if user is on a Windows Cloud OS device
                if (deviceInfo.browser.browserType === BrowserType.Edge && this.windowService.nativeWindow.external && 'getHostEnvironmentValue' in this.windowService.nativeWindow.external) {
                    const cloudOsGetHostEnvironmentKey = 'os-sku';
                    const cloudOsGetHostEnvironmentValues = ['178', '179'];

                    const externalFunctions = this.windowService.nativeWindow.external as any;
                    const result = externalFunctions.getHostEnvironmentValue(cloudOsGetHostEnvironmentKey);

                    if (result
                        && (result.indexOf(cloudOsGetHostEnvironmentValues[0]) !== -1 || result.indexOf(cloudOsGetHostEnvironmentValues[1]) !== -1)) {
                        resolve(this.getUnsupportedInstallInfo('Install is not supported on Windows CloudOS'));
                        return;
                    }
                }

                if (authType === AuthType.MSA) {
                    if (this.capabilitiesService.capabilities.workloadPublicDataEnabled) {
                        Promise.all([this.consumerPublicDataService.getConfig(), this.consumerInstallConfigService.getConfig()]).then(([consumerPublicDataConfig, consumerInstallConfig]) => {
                            const installerInfo = this.getGenericInstallForConsumer(platform, appFilter, consumerInstallConfig, consumerPublicDataConfig.hasBusinessLicense);
                            resolve(installerInfo);
                        }).catch(() => {
                                resolve(this.getGenericInstallForConsumer(platform, appFilter));
                            });
                    } else {
                        this.consumerInstallConfigService.getConfig().then(consumerInstallConfig => {
                        const installerInfo = this.getGenericInstallForConsumer(platform, appFilter, consumerInstallConfig);
                        resolve(installerInfo);
                        }).catch(() => {
                                resolve(this.getConsumerInstallerInfo());
                            });
                    }
                } else {
                    // Prepare commercial install experience on desktop
                    if (!this.installApiRoot) {
                        reject('Failed to get installable software. Install API root is not defined');
                        return;
                    }

                    const bitness = platform === PlatformType.PC ? this.bitness : null;
                    const platformVersion = platform === PlatformType.Mac ? deviceInfo.platformVersion.toString() : null;
                    const installableSoftwareStream = this.getInstallableSoftwareStream(PlatformType[platform].toLowerCase(), locale, appFilter, bitness, platformVersion);
                    installableSoftwareStream.results.pipe(take(1)).toPromise().then(data => {
                        if (!data || !data.software) {
                            reject('No data returned due to failure in both cache and network requests.');
                            return;
                        }

                        const installableSoftware = data.software;

                        if (installableSoftware.Status === InstallableSoftwareResultCode.Success) {
                            if (!installableSoftware.Apps || !installableSoftware.Apps.length) {
                                reject('No apps returned');
                                return;
                            }

                            const officeInstall = installableSoftware.Apps[0];
                            const installInfo = this.getDirectInstallerInfo(officeInstall);
                            resolve(installInfo);
                            return;
                        }

                        if (installableSoftware.Status === InstallableSoftwareResultCode.NoLicense) {
                            resolve(this.getInstallSoftwareInfo());
                            return;
                        }

                        if (installableSoftware.Status === InstallableSoftwareResultCode.InstallDisabled) {
                            resolve({
                                text: '',
                                ariaLabel: '',
                                url: '',
                                scenario: ButtonScenario.Hide
                            });
                            return;
                        }

                        reject(installableSoftware.Status);
                    });

                    installableSoftwareStream.initialCompletion.then(error => {
                        if (!error) {
                            return;
                        }

                        if (!(error.cacheError && error.networkError)) {
                            this.globalErrorHandler.handleCacheObservableError(error, ErrorId.J7V3F, ErrorId.eHDPU);
                            return;
                        }

                        //Both network and cache request failed. Error details in Unhandled_Exception.
                        reject(error);
                    });
                }
            }
        });

        this.deferredResponses.set(appFilter, deferredResponse);
        return this.deferredResponses.get(appFilter);
    }

    public getCustomInstaller(appId: string, officeMarcket: string): Promise<string> {
        if (!this.customInstallerPromise) {
            this.customInstallerPromise = new Map<string, Promise<string>>();
        }

        if ( this.customInstallerPromise.has(appId)) {
            return this.customInstallerPromise.get(appId);
        }

        const installerPromise = new Promise<string>((resolve, reject) => {
            const customInstallerStream = this.getCustomInstallerStream(appId, officeMarcket);
            customInstallerStream.results.pipe(take(1)).toPromise().then(data => {
                if (!data || !data.installerUrl) {
                    reject('No data returned due to failure in both cache and network requests.');
                    return;
                }

                resolve(data.installerUrl);
            });

            customInstallerStream.initialCompletion.then(error => {
                if (!error) {
                    return;
                }

                if (!(error.cacheError && error.networkError)) {
                    this.globalErrorHandler.handleCacheObservableError(error, ErrorId.YnO0h, ErrorId.ZGgqE);
                    return;
                }

                //Both network and cache request failed. Error details in Unhandled_Exception.
                reject(error);
            });
        });

        this.customInstallerPromise.set(appId, installerPromise);
        return installerPromise;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        if (paramNameInLowerCase === 'mockinstall' && this.mockRequestHeader) {
            this.config.headers.set(this.mockRequestHeader, value);
        }
    }

    /**
     * Calls portal download API to fetch a customized installer for a particular user. This installer is better for the user because it prevents them from having to
     * to sign in when installing Office. We should fall back on the generic installer if this call fails.
     * @returns An observable with the URL of a customized Office installer
     */
    private getCustomInstallerStream(appId: string, officeMarket: string): OperationStream<CustomInstaller> {
        if (!this.customInstallerStreamMap) {
            this.customInstallerStreamMap = new Map<string, OperationStream<CustomInstaller>>();
        }

        if (this.customInstallerStreamMap.has(appId)) {
            return this.customInstallerStreamMap.get(appId);
        }

        const locale = officeMarket.toLowerCase();
        const serviceUrl = this.installApiRoot ? `${this.installApiRoot}/apps/${appId}/downloadurl/${locale}/${this.bitnessDef}` : '';
        const installerFromCache = this.fetchCustomInstaller(serviceUrl, FetchStrategy.CacheOnly);
        const installerFromNetwork = this.fetchCustomInstaller(serviceUrl, FetchStrategy.NetworkFirst);
        const installerStream = ObservableHelper.createResultsWithCacheObservable(
            installerFromCache,
            installerFromNetwork,
            null,
            _ => {
                this.customInstallerStreamMap.delete(appId);
                this.customInstallerPromise.delete(appId);
                this.deferredResponses.delete(appId);
            });
        this.customInstallerStreamMap.set(appId, installerStream);
        return installerStream;
    }

    private getInstallSoftwareInfo(): InstallInfo {
        return {
            text: this.resources && this.resources.InstallSoftwareButtonText,
            ariaLabel: this.resources && this.resources.InstallAdditionalLinkAriaLabel,
            url: this.installConfig.commercialMySoftwarePageUrl,
            scenario: ButtonScenario.RedirectToMyAccountPage
        };
    }

    private getConsumerInstallerInfo(hasBusinessLicense: boolean = false): InstallInfo {
        return {
            text: this.resources ? this.locService.format(this.resources.InstallOfficeButtonText, this.resources.OfficeBrandShort) : '',
            ariaLabel: this.resources && this.resources.InstallConsumerMyAccountLinkAriaLabel,
            url: hasBusinessLicense ? this.installConfig.consumerSSUInstallLink : this.installConfig.consumerInstallLink,
            scenario: hasBusinessLicense ? ButtonScenario.SetupOffice : ButtonScenario.RedirectToMyAccountPage
        };
    }

    private getGenericInstallForConsumer(platform: PlatformType, appFilter: string, config?: ConsumerInstallConfig, hasBusinessLicense: boolean = false): InstallInfo {
        const subscriptionStatus = config && config.currentSkuInfo ? config.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
        if (platform === PlatformType.Mac
            && (subscriptionStatus === ConsumerSkuType.O365Home || subscriptionStatus === ConsumerSkuType.O365Personal || subscriptionStatus === ConsumerSkuType.O365Solo || hasBusinessLicense)
            && appFilter === 'Office') {
            return this.getConsumerDirectInstallerInfo(appFilter);
        }
        return this.getConsumerInstallerInfo(hasBusinessLicense);
    }

    private getConsumerDirectInstallerInfo(appFilter: string): InstallInfo {
        return {
            text: this.resources ? this.locService.format(this.resources.InstallOfficeButtonText, this.resources.OfficeBrandShort) : '',
            ariaLabel: this.resources ? this.locService.format(this.resources.InstallOfficeButtonAriaLabel, this.resources.OfficeBrandShort) : '',
            url: this.installConfig.consumerDirectInstallUrl,
            scenario: ButtonScenario.DirectInstall,
            appId: appFilter
        };
    }

    private getDirectInstallerInfo(officeInstaller: InstallableApp): InstallInfo {
        return {
            text: this.resources && this.resources.InstallOfficeLinkText,
            ariaLabel: this.resources ? this.locService.format(this.resources.InstallOfficeButtonAriaLabel, this.resources.InstallOfficeLinkText, this.resources.InstallOfficeDescription) : '',
            url: officeInstaller.StaticDownloadUrl,
            scenario: ButtonScenario.DirectInstall,
            appId: officeInstaller.Id
        };
    }

    private getMobileInstallerInfo(platform: PlatformType, mobileUrl: string): InstallInfo {
        let label: string;
        switch (platform) {
            case PlatformType.iPad:
                label = this.resources && this.resources.GetOfficeAppsButtonAriaLabelIpad;
                break;
            case PlatformType.iPhone:
                label = this.resources && this.resources.GetOfficeAppsButtonAriaLabelIphone;
                break;
            case PlatformType.AndroidPhone:
            case PlatformType.AndroidTablet:
                label = this.resources && this.resources.GetOfficeAppsButtonAriaLabelAndroid;
                break;
            default:
                // hide for Windows Phone and other mobile devices. Office Hub is installed by default on WP
                return this.getUnsupportedInstallInfo('Hidden for Windows Phone and unsupported mobile devices');
        }

        return {
            text: this.resources && this.resources.GetOfficeAppsButtonText,
            ariaLabel: label,
            url: mobileUrl,
            scenario: ButtonScenario.GetMobileApps
        };
    }

    private getUnsupportedInstallInfo(text: string): InstallInfo {
        return {
            text,
            ariaLabel: null,
            url: null,
            scenario: ButtonScenario.Hide
        };
    }

    /**
     * @param serviceUrl: service endpoint
     */
    private fetchInstallableSoftware(serviceUrl: string, fetchStrategy: FetchStrategy): Promise<InstallableSoftwareStream> {
        return new Promise<InstallableSoftwareStream>((resolve, reject) => {
            if (!serviceUrl) {
                reject(new XhrServiceError(null, 'Failed to get installable software. Service url is not defined'));
            }

            const callback = (promise: InstrumentedHttpPromise<InstallableSoftware>) => {
                promise
                    .instrument((result, params) => {
                        // Add additional instrumentation parameters upon successful response
                        if (result.ok && result.data) {
                            params[InstrumentationParam.ActualSize] = result.data.Apps ? result.data.Apps.length.toString() : '0';
                        }
                    })
                    .then(result => {
                        if (result.ok && result.data) {
                            resolve({
                                software: result.data,
                                fromCache: result.fromCache
                            });
                        } else {
                            reject(new XhrServiceError(result, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.GwqkI);
                    });
            };

            this.sendRequest(KnownInstrumentationEvents.InstallableAppsRequest, KnownRequestInstrumentationEvents.InstallableAppsRequest, serviceUrl, callback, reject, fetchStrategy);
        });
    }

    /**
     * @returns A promise with the URL of a customized Office installer
     */
    private fetchCustomInstaller(serviceUrl: string, fetchStrategy: FetchStrategy): Promise<CustomInstaller> {
        return new Promise<CustomInstaller>((resolve, reject) => {
            if (!serviceUrl) {
                reject(new XhrServiceError(null, 'Failed to get custom installer. Service url is not defined'));
                return;
            }

            const callback = (promise: InstrumentedHttpPromise<OfficeInstaller>) => {
                promise
                    .then(result => {
                        if (result.ok && result.data) {
                            if (result.data.Status >= CustomInstallerResultCode.Error) {
                                reject(result.data.Error);
                            } else {
                                const installerUrl = result.data.Url;
                                const fromCache = result.fromCache;
                                resolve({ installerUrl, fromCache });
                            }
                        } else {
                            reject(new XhrServiceError(result, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.XZu0C);
                    });
            };

            this.sendRequest(KnownInstrumentationEvents.InstallerRequest, KnownRequestInstrumentationEvents.InstallerRequest, serviceUrl, callback, reject, fetchStrategy);
        });
    }

    private getInstallableSoftwareStream(platformType: string, locale: string, appFilter: string, bitness?: string, platformVersion?: string): OperationStream<InstallableSoftwareStream> {
        if (!this.installableSoftwareStreamMap) {
            this.installableSoftwareStreamMap = new Map<string, OperationStream<InstallableSoftwareStream>>();
        }

        if (this.installableSoftwareStreamMap.has(appFilter)) {
            return this.installableSoftwareStreamMap.get(appFilter);
        }

        const bitnessPath = bitness ? `${bitness}/` : '';
        const platformVersionPath = platformVersion ? `${platformVersion}/` : '';
        const serviceUrl = encodeURI(`${this.installApiRoot}/apps/${locale}/${platformType}/${bitnessPath}${platformVersionPath}?$filter=(type eq '${appFilter}')`);
        const softwareFromCache = this.fetchInstallableSoftware(serviceUrl, FetchStrategy.CacheOnly);
        const softwareFromNetwork = this.fetchInstallableSoftware(serviceUrl, FetchStrategy.NetworkFirst);
        const softwareDataStream = ObservableHelper.createResultsWithCacheObservable(
            softwareFromCache,
            softwareFromNetwork,
            null,
            _ => {
                this.installableSoftwareStreamMap.delete(appFilter);
                this.deferredResponses.delete(appFilter);
            }
        );

        this.installableSoftwareStreamMap.set(appFilter, softwareDataStream);
        return softwareDataStream;
    }

    private sendRequest<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        serviceUrl: string,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        strategy: FetchStrategy
    ) {
        if (strategy === FetchStrategy.CacheOnly) {
            if (!this.installConfig.installApiSWEnabled) {
                errorCallback(new XhrServiceError(null, 'SW is not enabled for install apis.'));
                return;
            }

            super.get<T>(eventName, otelEventName, serviceUrl, callback, errorCallback, this.config, strategy);
            return;
        }

        // Handle network request
        strategy = this.installConfig.installApiSWEnabled ? strategy : FetchStrategy.NetworkOnly;
        super.get<T>(eventName, otelEventName, serviceUrl, callback, errorCallback, this.config, strategy);
    }
}
